import { useDispatch, useSelector } from 'react-redux'
import { Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type Field,
  moment,
  type NamedEntity,
  PermissionArea,
  Utils,
} from '@pbt/pbt-ui-components'

import EnumSelect, {
  type EnumSelectProps,
} from '~/components/common/inputs/EnumSelect'
import { ColorVariant } from '~/constants/colors'
import { TaskState, UncreatableTasks } from '~/constants/taskConstants'
import { useTaskMarkAsDoneDisabled } from '~/hooks/useTaskMarkAsDoneDisabled'
import { queueEasterEggEvent } from '~/store/actions/easterEgg'
import { editTaskState } from '~/store/actions/tasks'
import { useTaskEvent } from '~/store/hooks/tasks'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getTaskStates } from '~/store/reducers/constants'
import { getTasksIsLoading } from '~/store/reducers/tasks'
import { EasterEggEvents, type Task } from '~/types'

import { StylableAccentSelectItem } from './StylableAccentSelectItem'

const useStyles = makeStyles((theme) => ({
  errorState: {
    color: theme.colors.uiError,
    backgroundColor: theme.colors.shadesError,
    '&&:focus': {
      backgroundColor: theme.colors.shadesError,
    },
  },
}))

export interface TaskStatusSelectProps extends Partial<EnumSelectProps> {
  disabled?: boolean
  fullWidth?: boolean
  hideDue?: boolean
  task?: Task
}

export const TaskStatusSelectV2 = ({
  task,
  disabled: disabledProp,
  fullWidth,
  hideDue,
  ...rest
}: TaskStatusSelectProps) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getTasksIsLoading)
  const classes = useStyles()
  const permissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const TaskStates = useSelector(getTaskStates)
  const { subTypes: TaskSubTypes } = useTaskEvent()
  const isMarkAsDoneDisabled = useTaskMarkAsDoneDisabled(task)

  if (!task || R.isEmpty(task)) {
    return <Skeleton height={20} variant="text" width={70} />
  }

  const DueStateId = Utils.findConstantIdByName(TaskState.DUE, TaskStates)
  const OverdueStateId = Utils.findConstantIdByName(
    TaskState.OVERDUE,
    TaskStates,
  )
  const DoneStateId = Utils.findConstantIdByName(TaskState.DONE, TaskStates)

  const expired =
    task.stateId === OverdueStateId && moment().isAfter(task.dueDate)

  const filterFunc = (state: NamedEntity) => !expired || state.id !== DueStateId

  const FilteredTaskStates = TaskStates.filter(filterFunc)

  const setState = (stateId: string) => {
    if (stateId === DoneStateId) {
      dispatch(queueEasterEggEvent({ actionType: EasterEggEvents.TASK_DONE }))
    }
    dispatch(editTaskState(task.id, stateId))
  }

  const disabled =
    !permissions.update ||
    disabledProp ||
    UncreatableTasks.some(
      (type) =>
        task.typeId === Utils.findConstantByName(type, TaskSubTypes)?.id,
    ) ||
    isMarkAsDoneDisabled

  if (hideDue && DueStateId === task.stateId) {
    return <></>
  }

  const TaskSatetateColorMapping = {
    Overdue: ColorVariant.ERROR,
  }

  const selectItemProps = {
    classes: {
      errorState: classes.errorState,
    },
  }

  return (
    <EnumSelect
      accent
      Constant={FilteredTaskStates}
      SelectItemComponent={StylableAccentSelectItem}
      classes={{
        colorError: classes.errorState,
      }}
      colorVariantMap={TaskSatetateColorMapping}
      disabled={disabled || isLoading}
      field={
        {
          value: task.stateId,
          set: Utils.handleFormSelectInput(setState),
        } as Field
      }
      fullWidth={fullWidth}
      selectItemProps={selectItemProps}
      {...rest}
    />
  )
}
